import React, { useState } from "react";
import GetProductBrochures, { type GetProductBrochuresLabel } from "./GetProductBrochures";
import Icon from "@react/widget/Icon";
import Provider from "@module/apollo/client/Provider";
import { translateGlossaries as ts } from "@i18next/client/utils";
import type { ProductServiceBrochureDefaultEntry } from "@graphql/resources/productServiceBrochure";

interface ProductBrochuresLabel extends GetProductBrochuresLabel {

}

interface Props {
  currentSite: Page.Site;
  title?: string | null;
  volume: string;
  productService?: Array<ProductServiceBrochureDefaultEntry>;
  productServiceDefault: string;
  label: ProductBrochuresLabel;
  glossaries?: Record<string, string>;
}

const brochureTypes = [
  { label: "All", value: "*" },
  { label: "Product Brochure", value: "productBrochure" },
  { label: "Services Brochure", value: "servicesBrochure" },
  { label: "Solution Brochure", value: "solutionBrochure" },
  { label: "Technical Overview", value: "technicalOverview" },
];

const ProductBrochures: React.FC<Props> = ({ currentSite, title, volume, productService, productServiceDefault = "not 1", label, glossaries: gs }) => {
  const [searchVal, setSearchVal] = useState<string | undefined>(title || undefined);
  const [productServiceVal, setProductServiceVal] = useState<string>(productServiceDefault);
  const [brochureTypeVal, setBrochureTypeVal] = useState<string>("*");
  var _searchVal = searchVal;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8 items-end">
        {/* Product/Service Filter */}
        <div>
          <p className="text-sm mb-2 font-semibold">{ts(gs, "Product/Service")}</p>
          <select
            className="block w-full mt-1 rounded-md border-gray-100 focus:border-gray-300"
            defaultValue={productServiceVal}
            onChange={(e) => setProductServiceVal(e.target.value)}
          >
            <option value={productServiceDefault}>{ts(gs, "All")}</option>
            {productService?.map((option) => (
              <option key={option.id} value={option.id}>
                {ts(gs, option.title)}
              </option>
            ))}
          </select>
        </div>

        {/* Brochure Type Filter */}
        <div>
          <p className="text-sm mb-2 font-semibold">{ts(gs, "Brochure Type")}</p>
          <select
            className="block w-full mt-1 rounded-md border-gray-100 focus:border-gray-300"
            defaultValue={brochureTypeVal}
            onChange={(e) => setBrochureTypeVal(e.target.value)}
          >
            {brochureTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {ts(gs, type.label)}
              </option>
            ))}
          </select>
        </div>

        {/* Search Bar and Reset */}
        <div className="flex items-center gap-4 sm:col-span-2">
          <div className="relative inline-block align-middle border overflow-hidden hover:border-gray-300  focus-within:border-gray-300 bg-white border-gray-100 rounded-md sm:flex-1 [&_.reset]:text-xl [&_.reset]:top-2.5 [&_.reset]:right-19.5 [&_.submit]:text-2xl [&_.submit]:py-1 [&_.submit]:my-1 [&_.submit]:border-l [&_.submit]:border-l-[21242726] [&_.submit]:px-4.5 w-full">
            <div className="group/form flex w-full search">
              <input
                className="input flex-1 text-sm text-gray pl-3 py-1.5 pr-0 outline-0 border-0 [&[data-value]+.reset]:block"
                type="text"
                name="title"
                defaultValue={searchVal}
                placeholder={ts(gs, "Search for...")}
                onChange={e => _searchVal = e.target.value || undefined}
              />
              <button
                title="Search"
                className="submit flex-none px-3 py-1 text-placeholder hover:text-primary"
                type="button"
                onClick={() => setSearchVal(_searchVal)}
              >
                <Icon className="block text-placeholder" name="search" />
              </button>
            </div>
          </div>
          <a
            className="flex items-center gap-2 font-semibold"
            href={`${currentSite.localePath}/resources/product-brochures`}
          >
            {ts(gs, "Reset")}&nbsp;
            <img
              src="https://assets.avepoint.com/upload/images/partners/images/partner/icons/rotate.svg"
              width={16}
              height={16}
              alt="rotate"
            />
          </a>
        </div>
      </div>

      {/* Brochure List */}
      <div>
        <Provider>
          <GetProductBrochures
            currentSite={currentSite}
            label={label}
            title={searchVal}
            volume={volume}
            productServiceVal={productServiceVal}
            brochureTypeVal={brochureTypeVal}
          />
        </Provider>
      </div>
    </>
  );
};

export default ProductBrochures;